// @givmo/app-admin — the one failure presenter for the whole company sign-in surface (AS-WEB6).
//
// Extracted from `sso-section.tsx` by AS-WEB7 so the directory-sync panel can present its refusals
// the same way without the two files importing each other. Nothing about the behaviour changed.

import { firstErrorCode, isGivmoError, supportReference } from '@givmo/api-client';
import { testIdProp } from '@givmo/design-system';
import { SSO_COPY } from './security-copy';

/**
 * One presenter for every failure on this surface.
 *
 * THE BACKEND'S SENTENCE IS THE COPY. Every refusal here is a typed `ValidationException` whose
 * message is written for the IT admin who has to fix it and, in the domain-check case, is the ONLY
 * thing distinguishing two failures with opposite remedies. Substituting our own wording would throw
 * that away. A non-`GivmoError` — a transport failure, a parse failure — has no such sentence, and
 * gets the caller's DRAFT fallback.
 *
 * ONE EXCEPTION, and it is deliberate: `SSO_REDIRECT_URI_NOT_ALLOWED`. Its server message ("That
 * redirect target is not registered for company sign-in") is correct and would read to an employer
 * as something THEY misconfigured. It is an operator fact about the environment — the admin origin
 * has not been added to `EMPLOYER_SSO_ALLOWED_REDIRECT_URIS` — and nothing the Owner can do changes
 * it, so the honest-claims rail wins over the render-the-server rule for this one code.
 *
 * The `error_code` is rendered beside the message, tertiary, because it is the string a support
 * conversation needs. Raw error bodies are never rendered.
 */
export function SsoErrorNote({
  error,
  fallback,
  testId,
}: {
  error: unknown;
  fallback: string;
  testId: string;
}) {
  const code = isGivmoError(error) ? firstErrorCode(error) : null;
  const message =
    code === 'SSO_REDIRECT_URI_NOT_ALLOWED'
      ? SSO_COPY.testRedirectNotRegistered
      : isGivmoError(error)
        ? error.message
        : fallback;
  const reference = isGivmoError(error) ? supportReference(error) : null;

  return (
    <div className="flex flex-col items-start gap-4" {...testIdProp(testId)}>
      <p className="font-sans text-s13 font-w500 leading-h15 tracking-normal text-danger-700">
        {message}
      </p>
      {(code !== null || reference !== null) && (
        <p className="font-sans text-s12 font-w400 tracking-normal text-text-tertiary">
          {[code, reference].filter((part) => part !== null).join(' · ')}
        </p>
      )}
    </div>
  );
}
